import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { Download, ChevronDown, ChevronRight, ArrowUpRight, ArrowDownRight, Minus, Pencil, Check, X, Maximize, Minimize } from "lucide-react";
import { ScorecardPresentation, fmtFieldClose, fmtCloseRate, fmtObp } from "./PresentationViews";
import { EntryWeekSwitcher, useUrlWeek } from "@/pages/dashboard/kpi/EntryWeekSwitcher";

function complianceTone(pct) {
  if (pct >= 75) return { text: "text-[#0e8a68]", bg: "bg-[#e6f9f2]", border: "border-[#c1eddc]", bar: "bg-[#0e8a68]" };
  if (pct >= 50) return { text: "text-[#8a6a1c]", bg: "bg-[#fff6dc]", border: "border-[#f0dfa5]", bar: "bg-[#f4b400]" };
  return { text: "text-[#ef5757]", bg: "bg-[#fdecec]", border: "border-[#f4c9c9]", bar: "bg-[#ef5757]" };
}

function TrendArrow({ trend, delta }) {
  if (!trend || trend === "flat") {
    return (
      <span title="No change vs last week" className="inline-flex items-center gap-0.5 text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">
        <Minus size={13} strokeWidth={2} /> flat
      </span>
    );
  }
  const up = trend === "up";
  const cls = up ? "text-[#0e8a68]" : "text-[#ef5757]";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span title={`${up ? "+" : ""}$${Math.abs(delta || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} vs last week`}
          className={`inline-flex items-center gap-0.5 text-[11.5px] font-mono-data font-bold ${cls}`}
          data-testid={`scorecard-trend-arrow`}>
      <Icon size={15} strokeWidth={2.2} />
      {up ? "+" : "−"}${Math.abs(delta || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
    </span>
  );
}

function EditableTarget({ label, value, field, techName, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const commit = async () => {
    setErr(null);
    const t = draft.trim();
    const payload = t === "" ? null : Number(t);
    if (t !== "" && (Number.isNaN(payload) || payload < 0 || payload > 100)) {
      setErr("0–100");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/kpi/targets", { tech_name: techName, [field]: payload });
      onSaved?.();
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-md border border-[#eef1f6] p-3" data-testid={`scorecard-target-cell-${techName}-${field}`}>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] flex items-center justify-between gap-2">
        <span>{label}</span>
        {!editing && (
          <button
            onClick={() => { setDraft(value == null ? "" : String(value)); setEditing(true); }}
            data-testid={`scorecard-target-edit-${techName}-${field}`}
            aria-label={`Edit ${label}`}
            className="text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] p-0.5"
          >
            <Pencil size={11} strokeWidth={1.8} />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1 flex items-center gap-1">
          <input
            type="number" min="0" max="100" step="0.5" autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
            data-testid={`scorecard-target-input-${techName}-${field}`}
            className="w-16 bg-white border border-[#e7e9ee] rounded px-1.5 py-0.5 text-[13px] font-mono-data font-bold text-[#0e1420] outline-none focus:border-[#0072bc]"
          />
          <span className="text-[13px] font-mono-data text-[color:var(--cc-ink-muted)]">%</span>
          <button onClick={commit} disabled={busy} aria-label="Save"
                  data-testid={`scorecard-target-save-${techName}-${field}`}
                  className="text-[#0e8a68] hover:text-[#0b6f52] p-0.5"><Check size={13} strokeWidth={2.2} /></button>
          <button onClick={() => setEditing(false)} disabled={busy} aria-label="Cancel"
                  className="text-[color:var(--cc-ink-muted)] hover:text-[#c73535] p-0.5"><X size={13} strokeWidth={2.2} /></button>
        </div>
      ) : (
        <div className="font-mono-data font-bold text-[15px] text-[#0e1420] mt-1">{value == null ? "—" : `${value}%`}</div>
      )}
      {err && <div className="text-[10.5px] text-[#c73535] mt-0.5">{err}</div>}
    </div>
  );
}

function TechCard({ t, onSaved }) {
  const [open, setOpen] = useState(false);

  // ---- Standby row: dimmer, no per-item, no editable targets ----
  if (t.is_standby) {
    return (
      <div className="bg-[#fafbfc] border border-dashed border-[#e7e9ee] rounded-xl p-4 flex items-center gap-3"
           data-testid={`scorecard-standby-${t.tech_name}`}>
        <span className="text-[10px] font-mono-data font-bold uppercase tracking-widest bg-[#e5e7eb] text-[#4b5563] border border-[#9ca3af] rounded px-2 py-0.5">
          STANDBY
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-[15px] text-[#0e1420]">{t.tech_name}</div>
          <div className="text-[11px] text-[color:var(--cc-ink-muted)]">{t.unit} · Unassigned or on standby this week — no jobs recorded</div>
        </div>
        {t.field_close_dispatched > 0 && (
          <span className="text-[11px] font-mono-data font-semibold uppercase tracking-widest border rounded px-2 py-1 text-[#5d6a80] bg-white border-[#dde3ec]"
                data-testid={`scorecard-field-close-${t.tech_name}`}>
            Field close · {fmtFieldClose(t)}
          </span>
        )}
      </div>
    );
  }

  const overall = complianceTone(t.kpi_compliance_pct);
  // Compliance badge (2026-07-24 fix per Brett): show "INCOMPLETE" whenever
  // any required item is unanswered, not a partial %. Only render as
  // COMPLIANT at 100% with zero unanswered required items. Backend now
  // ships `compliance_state` explicitly so this rendering stays server-driven.
  const isIncomplete = t.compliance_state === "INCOMPLETE" || (t.required_unanswered ?? 0) > 0;
  const isCompliant = t.compliance_state === "COMPLIANT";
  const incompleteTone = "text-[#8a5b00] bg-[#fff4d6] border-[#f3d97a]";
  const compliantTone = "text-[#0e5a48] bg-[#e6f9f2] border-[#c1eddc]";
  const complianceBadgeTone = isIncomplete
    ? incompleteTone
    : (isCompliant ? compliantTone : `${overall.text} ${overall.bg} ${overall.border}`);
  const complianceBadgeText = isIncomplete
    ? `INCOMPLETE · ${t.required_unanswered ?? 0} required unanswered`
    : (isCompliant ? "COMPLIANT · 100%" : `KPI compliance · ${t.kpi_compliance_pct}%`);
  const crTone = t.close_rate_pct == null
    ? "text-[#5d6a80] bg-[#f5f6f8] border-[#dde3ec]"
    : `${complianceTone(t.close_rate_pct).text} ${complianceTone(t.close_rate_pct).bg} ${complianceTone(t.close_rate_pct).border}`;
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid={`scorecard-tech-${t.tech_name}`}>
      <div className="p-5 border-b border-[#eef1f6]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display font-bold text-[17px] text-[#0e1420]">{t.tech_name}</div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              {/* CC-242: OBP from the ST row when present, else "Not connected" */}
              {t.obp_source === "st_tech_performance" ? (
                <span className="text-[11px] font-mono-data font-semibold uppercase tracking-widest border border-[#dde3ec] text-[#0e1420] bg-white rounded px-2 py-0.5"
                      title="OBP from the ServiceTitan Technician Performance Board" data-testid={`scorecard-obp-${t.tech_name}`}>
                  OBP · {fmtObp(t)}
                </span>
              ) : (
                <span className="text-[11px] font-mono-data font-semibold uppercase tracking-widest border border-dashed border-[#c9cfd8] text-[#5d6a80] bg-[#f5f6f8] rounded px-2 py-0.5"
                      title="OBP is not connected to a live data source yet" data-testid={`scorecard-obp-not-connected-${t.tech_name}`}>
                  OBP · Not connected
                </span>
              )}
              <span className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">{t.job_count} jobs</span>
              <TrendArrow trend={t.trend} delta={t.wow_delta} />
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">{t.unit}</div>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Completed Revenue</div>
            <div className="font-mono-data font-bold text-[20px] text-[#0e1420]">${t.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span
            className={`text-[11px] font-mono-data font-semibold uppercase tracking-widest border rounded px-2 py-1 ${complianceBadgeTone}`}
            data-testid={`scorecard-compliance-${t.tech_name}`}
          >
            {complianceBadgeText}
          </span>
          <span
            className={`text-[11px] font-mono-data font-semibold uppercase tracking-widest border rounded px-2 py-1 ${crTone}`}
            title="Close rate from the ServiceTitan Technician Performance Board."
            data-testid={`scorecard-close-rate-${t.tech_name}`}
          >
            Close rate (ST) · {fmtCloseRate(t)}
          </span>
        </div>
        <div className="mt-1.5 text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]"
             title="Jobs invoiced / jobs dispatched this week (ServiceTitan invoiced_at / dispatched_at)."
             data-testid={`scorecard-field-close-${t.tech_name}`}>
          Field close (invoiced/dispatched) · {fmtFieldClose(t)}
        </div>
      </div>

      {/* per-item grid */}
      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {t.per_item.map((it) => {
          const blanks = it.unanswered || 0;  // CC-219: MANUAL blanks -> INCOMPLETE amber
          const tone = blanks > 0
            ? { text: "text-[#8a5b00]", bar: "bg-[#f3d97a]" }
            : it.pct == null  // CC-211: no YES/NO answers for this item -> N/A, neutral
            ? { text: "text-[color:var(--cc-ink-muted)]", bar: "bg-[#c9cfd8]" } : complianceTone(it.pct);
          return (
            <div key={it.key} className="flex items-center gap-3 text-[12.5px]" data-testid={`scorecard-item-${t.tech_name}-${it.key}`}>
              <span className={`inline-block w-1.5 h-6 rounded ${tone.bar}`} />
              <span className="min-w-0 flex-1 truncate text-[#0e1420]">{it.label}</span>
              <span className="text-[9.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">{it.source}</span>
              <span className={`font-mono-data font-semibold ${tone.text} text-right whitespace-nowrap`} data-testid={`scorecard-item-value-${t.tech_name}-${it.key}`}>
                {it.pct == null ? "N/A" : `${it.pct}%`}{blanks > 0 && ` · ${blanks} blank`}
              </span>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`scorecard-target-toggle-${t.tech_name}`}
        className="w-full flex items-center justify-between px-5 py-3 border-t border-[#eef1f6] text-[12px] font-semibold text-[#0e1420] hover:bg-[#fafbfc]"
      >
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Target sub-metrics
        </span>
        <span className="text-[color:var(--cc-ink-muted)] text-[11px]">Receipts · Vehicle · Meeting</span>
      </button>
      {open && (
        <div className="px-5 pb-5 grid grid-cols-3 gap-3" data-testid={`scorecard-target-${t.tech_name}`}>
          <EditableTarget label="Receipts" value={t.target.receipts_pct} field="receipts_pct" techName={t.tech_name} onSaved={onSaved} />
          <EditableTarget label="Vehicle Inspection" value={t.target.vehicle_inspection_pct} field="vehicle_inspection_pct" techName={t.tech_name} onSaved={onSaved} />
          <div className="rounded-md border border-[#eef1f6] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Meeting attendance</div>
            <div className="font-mono-data font-bold text-[15px] text-[#0e1420] mt-1">
              {t.target.meeting_attendance === null || t.target.meeting_attendance === undefined
                ? "—"
                : t.target.meeting_attendance ? "Present" : "Absent"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ScorecardMode() {
  const [data, setData] = useState(null);  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  // CC-213: URL-synced week (?week=YYYY-MM-DD), same contract as Entry and Wall.
  const [week, setWeek] = useUrlWeek();

  const load = async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const { data: resp } = await api.get(`/kpi/scorecard?week=${encodeURIComponent(week)}`);
      setData(resp);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setData(null);
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  };
  useEffect(() => { load(); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps
  const switcher = <EntryWeekSwitcher currentWeekOf={data?.week_of} setWeek={setWeek} includeEmpty testIdPrefix="scorecard-week" />;

  // Full Screen toggle (2026-08-11) — mirrors the Wall control. Triggers the
  // scoreboard-scale presentation layout for the Monday meeting on the 80" TV.
  const wrapRef = useRef(null);
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const onChange = () => setFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  };

  const isEmpty = loadState.status === "ok" && (!data || !data.techs || data.techs.length === 0);
  if (loadState.status !== "ok" || isEmpty) {
    return (
      <div className="space-y-4" data-testid="kpi-scorecard-empty-wrap">
      {loadState.status !== "loading" && switcher}
      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={isEmpty}
        onRetry={load}
        emptyTitle="No scorecard rows yet"
        emptyBody={<>No jobs logged for this week yet. Use the week switcher above to view another week.</>}
        testIdPrefix="kpi-scorecard"
      />
      </div>
    );
  }

  const exportCsv = () => {
    const rows = [
      ["Tech", "Unit", "OBP", "Below floor", "Revenue", "KPI compliance %", "Close rate %"],
      ...data.techs.map((t) => [
        t.tech_name, t.unit, fmtObp(t), t.obp_source === "st_tech_performance" ? t.below_floor : "Not connected", t.revenue, t.kpi_compliance_pct, t.close_rate_pct ?? "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scorecard-${data.week_of}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Presentation-mode short-circuit
  if (fs) {
    return (
      <div ref={wrapRef} data-testid="kpi-scorecard-mode" className="fixed inset-0 z-40 bg-white p-8 overflow-y-auto">
        <div className="flex justify-end mb-6">
          <button
            onClick={toggleFullscreen}
            data-testid="scorecard-fullscreen-exit"
            className="inline-flex items-center gap-2 text-[16px] font-bold px-4 py-3 rounded-lg border-2 border-[#0e1420] bg-white text-[#0e1420] hover:bg-[#f5f6f8]"
          >
            <Minimize size={18} strokeWidth={2} />
            Exit Full Screen
          </button>
        </div>
        <ScorecardPresentation data={data} />
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="space-y-4" data-testid="kpi-scorecard-mode">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-display font-bold text-[19px] text-[#0e1420]">Weekly Scorecard</div>
          <div className="text-[12.5px] text-[color:var(--cc-ink-muted)]">{data.week_range}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {switcher}
          <button onClick={toggleFullscreen} data-testid="scorecard-fullscreen-btn" title="Full Screen for Monday meeting"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-lg border border-[#e7e9ee] bg-white text-[#0e1420] hover:bg-[#f5f6f8]">
            <Maximize size={13} strokeWidth={1.8} />
            Full Screen
          </button>
          <button onClick={exportCsv} data-testid="scorecard-export-btn" className="cc-btn-primary text-[12.5px] py-2 px-3">
            <Download size={14} strokeWidth={1.8} /> Export
          </button>
        </div>
      </div>

      {/* Monday-morning banner (2026-02) — shows when the resolved week is the
          current calendar Monday AND at least 3 techs are STANDBY. Prevents the
          "half-empty scorecard panic" pattern where a manager opens Monday
          morning, sees mostly STANDBY cards, and assumes the pipeline is
          broken. Offers a one-click switch to last week's completed data. */}
      {(() => {
        const now = new Date();
        const dow = now.getUTCDay();               // 0=Sun, 1=Mon, ... 6=Sat
        const daysBackToMon = dow === 0 ? 6 : dow - 1;
        const todayMon = new Date(now); todayMon.setUTCDate(now.getUTCDate() - daysBackToMon);
        const iso = (d) => d.toISOString().slice(0, 10);
        const todayMonISO = iso(todayMon);
        const lastMon = new Date(todayMon); lastMon.setUTCDate(todayMon.getUTCDate() - 7);
        const lastMonISO = iso(lastMon);
        const lastSun = new Date(lastMon); lastSun.setUTCDate(lastMon.getUTCDate() + 6);
        const isCurrentWeek = data.week_of === todayMonISO;
        const standbyCount = (data.techs || []).filter((t) => t.is_standby).length;
        const showBanner = isCurrentWeek && standbyCount >= 3;
        const viewingPriorWeek = week !== "this" && !isCurrentWeek;

        if (showBanner) {
          return (
            <div
              data-testid="scorecard-monday-banner"
              className="rounded-lg border border-[#f0dfa5] bg-[#fff6dc] px-4 py-3 flex items-start gap-3"
            >
              <div className="text-[13px] leading-snug text-[#8a6a1c] flex-1">
                <div className="font-semibold">This week just started.</div>
                <div className="mt-0.5">
                  Jobs will populate as techs log them. {standbyCount} of {data.techs.length} techs are STANDBY right now — expected for Monday morning, not a data issue.
                </div>
              </div>
              <button
                type="button"
                data-testid="scorecard-view-last-week-btn"
                onClick={() => setWeek(lastMonISO)}
                className="shrink-0 text-[12px] font-semibold uppercase tracking-widest border border-[#8a6a1c] text-[#8a6a1c] bg-white rounded px-3 py-1.5 hover:bg-[#8a6a1c] hover:text-white transition-colors"
                title={`View week ending ${lastSun.toISOString().slice(0, 10)}`}
              >
                Show week ending {lastSun.toISOString().slice(5, 10).replace("-", "/")}
              </button>
            </div>
          );
        }
        if (viewingPriorWeek) {
          return (
            <div
              data-testid="scorecard-prior-week-banner"
              className="rounded-lg border border-[#0072bc] bg-[#eaf4fc] px-4 py-2 flex items-center gap-3"
            >
              <div className="text-[12.5px] text-[#0e1420] flex-1">
                Viewing prior week: <span className="font-mono-data font-semibold">{data.week_of}</span>
              </div>
              <button
                type="button"
                data-testid="scorecard-back-to-current-btn"
                onClick={() => setWeek("this")}
                className="text-[11.5px] font-semibold uppercase tracking-widest text-[#0072bc] hover:underline"
              >
                Back to this week
              </button>
            </div>
          );
        }
        return null;
      })()}

      {/* Tech Goals — This Week (read-only mirror of Wall's editable goals) */}
      <section data-testid="scorecard-tech-goals">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
          <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Tech Goals · This Week</span>
          <span className="text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">
            · Set on the Wall
          </span>
          <span className="ml-auto text-[10px] font-mono-data uppercase tracking-widest bg-[#fff6dc] text-[#8a6a1c] border border-[#f0dfa5] rounded px-2 py-0.5 font-bold"
                title="$2,500 weekly OBP baseline — kept separate from per-tech weekly revenue goals">
            OBP Floor $2,500 · fixed
          </span>
        </div>
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto">
          <table className="w-full min-w-[640px] text-[13px]">
            <thead className="bg-[#f5f6f8] text-[10.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-bold">
              <tr>
                <th className="text-left py-2 px-4">Technician</th>
                <th className="text-right py-2 px-4">Revenue</th>
                <th className="text-right py-2 px-4">Weekly Goal</th>
                <th className="text-right py-2 px-4">% of Goal</th>
              </tr>
            </thead>
            <tbody>
              {data.techs.filter((t) => !t.is_standby).map((t) => {
                const pct = t.pct_of_goal;
                const pctColor = pct == null
                  ? "text-[color:var(--cc-ink-muted)]"
                  : pct >= 100 ? "text-[#0e8a68]"
                  : pct >= 70 ? "text-[#8a6a1c]"
                  : "text-[#ef5757]";
                return (
                  <tr key={t.tech_name} className="border-t border-[#eef1f6]" data-testid={`scorecard-goal-row-${t.tech_name}`}>
                    <td className="py-2 px-4 font-semibold text-[#0e1420]">{t.tech_name}</td>
                    <td className="py-2 px-4 text-right font-mono-data font-semibold">
                      ${t.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-4 text-right font-mono-data font-semibold text-[#0e1420]">
                      {t.has_goal
                        ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <span>${Number(t.weekly_goal).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            {t.weekly_goal_carried_from && (
                              <span
                                className="text-[10px] font-normal text-[color:var(--cc-ink-muted)]"
                                title={`No explicit weekly goal was entered for this week. Falling back to the goal last set on ${t.weekly_goal_carried_from}.`}
                                data-testid={`scorecard-goal-carried-${t.tech_name}`}
                              >
                                carried over from {t.weekly_goal_carried_from}
                              </span>
                            )}
                          </div>
                        )
                        : <span className="text-[color:var(--cc-ink-muted)]">not set</span>}
                    </td>
                    <td className={`py-2 px-4 text-right font-mono-data font-bold ${pctColor}`}>
                      {pct != null ? `${pct}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        {data.techs.map((t) => <TechCard key={t.tech_name} t={t} onSaved={load} />)}
      </div>
    </div>
  );
}
